import { useUnit } from 'effector-react'
import { Tooltip } from 'antd'
import { $userRole } from 'src/features/user'
import { path, role } from 'src/libs'
import { getFinsystemNumberFormat } from 'src/libs/utils'
import { $generateBalanceFilter } from 'src/pages/finSystem/balanceSummary/widgets/generalBalanceFilter/model'
import { ActiveBalancesData, DataType } from './types'
import {
  getRolesByGuardRules,
  GUARD_RULES,
} from 'src/features/session/constants'

type Props = {
  url: string
  title: string
}

function PermissionLink({ url, title }: Props) {
  const userRole = useUnit($userRole)
  const canView =
    userRole &&
    userRole.includes(role.logged()) &&
    getRolesByGuardRules(GUARD_RULES.VIEW_FIN_STATEMENT).some((r) =>
      userRole.includes(r),
    )

  if (canView) {
    return (
      <a href={url} target="_blank">
        {title}
      </a>
    )
  }
  return <div>{title}</div>
}

export const mapActiveBalances = (
  data: ActiveBalancesData,
  paymentSystemId?: string,
): DataType[] => {
  const generateBalanceFilter = useUnit($generateBalanceFilter)
  const showTransfers = Boolean(paymentSystemId)

  return [
    {
      key: 1,
      name: (
        <Tooltip title="Депозит - вывод" placement="topLeft">
          <div>Turnover</div>
        </Tooltip>
      ),
      shouldBeGray: true,
      wallet: getFinsystemNumberFormat(data.turnover.value, 2),
      children: [
        {
          key: 11,
          name: (
            <Tooltip title="Сколько мы получили на счет от клиентов">
              <div>Deposit</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.turnover.deposit, 2),
        },
        {
          key: 12,
          name: (
            <Tooltip title="Сколько у нас вывели со счетов клиенты">
              <div>Withdrawal</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.turnover.withdrawal, 2),
        },
      ],
    },
    {
      key: 2,
      name: (
        <Tooltip title="Бюджетные платежи + Дивиденды + Другие платежи">
          <div>Liabilities</div>
        </Tooltip>
      ),
      shouldBeGray: true,
      wallet: getFinsystemNumberFormat(data.liabilities.value, 2),
      children: [
        {
          key: 21,
          name: 'Budget payments',
          wallet: getFinsystemNumberFormat(data.liabilities.budgetPayment, 2),
        },
        {
          key: 22,
          name: 'Other payments',
          wallet: getFinsystemNumberFormat(data.liabilities.otherPayment, 2),
        },
        {
          key: 23,
          name: 'Investments',
          wallet: getFinsystemNumberFormat(data.investment, 2),
        },
        {
          key: 24,
          name: 'Earnings',
          wallet: getFinsystemNumberFormat(data.earning, 2),
        },
        {
          key: 25,
          name: (
            <PermissionLink
              url={path.report.dividends.entry(
                generateBalanceFilter.date.dateFrom,
                generateBalanceFilter.date.dateTo,
                '-amountUsd',
              )}
              title="Dividends"
            />
          ),
          wallet: getFinsystemNumberFormat(data.liabilities.dividend, 2),
        },
      ],
    },
    {
      key: 3,
      name: (
        <Tooltip
          title="Комиссии по транзакциям депозит/вывод + комиссии по бюджетным платежам + остальные комиссии"
          placement="topLeft"
        >
          <div>Costs</div>
        </Tooltip>
      ),
      shouldBeGray: true,
      wallet: getFinsystemNumberFormat(data.costs.value, 2),
      children: [
        {
          key: 31,
          name: (
            <Tooltip title="Комиссии за клиентские транзакции. Для информации, не учитывается в расчетах">
              <PermissionLink
                url={path.report.commissions.entry(
                  generateBalanceFilter.date.dateFrom,
                  generateBalanceFilter.date.dateTo,
                  '-commissionAmountUsd',
                )}
                title="Transaction commission"
              />
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.costs.transactionCommission, 2),
        },
        {
          key: 32,
          name: (
            <Tooltip title="Комиссии за платежи">
              <div>Payments commission</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.costs.paymentCommission, 2),
        },
        {
          key: 33,
          name: (
            <Tooltip title="Ручные операции списания/начисления комиссии">
              <div>Commission</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.costs.commission, 2),
        },
      ],
    },
    {
      key: 4,
      name: (
        <Tooltip title="Сумма созданных операций долгов за период по полю Balance Due (остаток долга)">
          <div>Debt</div>
        </Tooltip>
      ),
      wallet: getFinsystemNumberFormat(data.debt, 2),
    },
    {
      key: 5,
      name: (
        <Tooltip
          title="Сколько сейчас на счетах LP с учетом торгов"
          placement="topLeft"
        >
          <div>LP Balance</div>
        </Tooltip>
      ),
      shouldBeGray: true,
      wallet: getFinsystemNumberFormat(data.lpBalance.value, 2),
      children: [
        {
          key: 51,
          name: (
            <Tooltip title="Сколько перевели на LP">
              <div>LP Investments</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.lpBalance.lpInvest, 2),
        },
        {
          key: 52,
          name: (
            <Tooltip title="LP конечный баланс - все транзакции за период - LP начальный баланс">
              <div>LP Trade</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.lpBalance.lpTrade, 2),
        },
        {
          key: 53,
          name: (
            <Tooltip title="Сколько получили обратно от LP">
              <div>LP Returns</div>
            </Tooltip>
          ),
          wallet: getFinsystemNumberFormat(data.lpBalance.lpReturn, 2),
        },
      ],
    },
    ...(showTransfers
      ? [
          {
            key: 6,
            name: (
              <Tooltip
                title="Входящие переводы между нашими кошельками - исходящие переводы между нашими кошельками"
                placement="topLeft"
              >
                <div>Transfers</div>
              </Tooltip>
            ),
            wallet: getFinsystemNumberFormat(data.transfers.value, 2),
          },
        ]
      : []),
    {
      key: 7,
      name: (
        <Tooltip title="Курсовая разница за выбранный период">
          <PermissionLink
            url={path.report.exDifference.entry(
              generateBalanceFilter.date.dateFrom,
              generateBalanceFilter.date.dateTo,
            )}
            title="Exchange difference"
          />
        </Tooltip>
      ),
      wallet: getFinsystemNumberFormat(data.exchangeDifference, 2),
    },
    {
      key: 8,
      name: (
        <Tooltip title="Конечный остаток на всех кошельках по курсу на конец периода (берется из баланса кошельков, а не из расчетов таблицы)">
          <div>Final balance</div>
        </Tooltip>
      ),
      wallet: '',
      result: getFinsystemNumberFormat(data.finalBalanceUsd, 2),
    },
    {
      key: 9,
      name: (
        <Tooltip title="Разница между начальным и конечным остатком">
          <div>Balance change</div>
        </Tooltip>
      ),
      wallet: '',
      result: getFinsystemNumberFormat(data.balanceChange, 2),
    },
    {
      key: 10,
      name: (
        <Tooltip
          title="Проверка final balance по расчетам, а не по конечному балансу кошелька (для проверки) баланс на начало + депозиты - выводы - бюджетные - комиссия + инвестиции + доходы - дивиденды - ЛП инвестиции + ЛП дивиденды + входящие переводы - исходящие переводы
"
        >
          <div>Проверка final balance</div>
        </Tooltip>
      ),
      wallet: '',
      //баланс на начало + депозиты - выводы - бюджетные + инвестиции + доходы - дивиденды - комиссия - ЛП инвестиции + ЛП дивиденды + входящие переводы - исходящие переводы + Exchange difference + lp balance который change
      result:
        paymentSystemId === '0'
          ? getFinsystemNumberFormat(
              data.openingBalance -
                data.costs.commission +
                data.transfers.value,
              2,
            )
          : getFinsystemNumberFormat(
              data.openingBalance -
                data.inProgress.openingBalance +
                data.inProgress.finalBalance +
                data.turnover.deposit -
                data.turnover.withdrawal -
                data.liabilities.budgetPayment -
                data.liabilities.otherPayment +
                data.investment +
                data.earning -
                data.debt -
                data.liabilities.dividend -
                data.lpBalance.lpInvest +
                data.lpBalance.lpReturn +
                data.transfers.inInt -
                data.transfers.outInt -
                data.costs.paymentCommission -
                data.costs.commission +
                data.exchangeDifference +
                data.lpBalance.value,
              2,
            ),
    },
  ]
}
